/**
 * Instructor dashboard (design D2): pure helpers for the three lanes
 * (Live Q&A, Extension requests, To grade) and the course/week facts the
 * live banner, Quick Add and course cards use. No React, no fetching.
 */
import { DateTime } from 'luxon';
import { isAnswered } from '../../sessionQna/qnaHelpers';
import { collapseWeeks } from '../../assignments/assignments/instructorAssignmentAgenda';

export const ZONE = 'America/Chicago';
export const LANE_LIMIT = 2; // every card on the dashboard is the same size (design U1)

export const chicagoNow = () => DateTime.now().setZone(ZONE);

const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

/** DB value (ISO, 'YYYY-MM-DD HH:mm:ss' UTC, or Date) → Chicago DateTime | null. */
export function toChicago(value) {
  if (!value) return null;
  const dt = value instanceof Date
    ? DateTime.fromJSDate(value)
    : DateTime.fromISO(String(value).replace(' ', 'T'), { zone: 'utc' });
  return dt.isValid ? dt.setZone(ZONE) : null;
}

/** Class schedule is separate from the attendance check-in window. */
export function instructorClassWindow(session, now = chicagoNow()) {
  const date = String(session.date || toChicago(session.start_time)?.toISODate() || '').slice(0, 10);
  const at = time => DateTime.fromISO(`${date}T${String(time).slice(0, 8)}`, { zone: ZONE });
  let start = session.virtual_start_time ? at(session.virtual_start_time) : null;
  let end = session.virtual_end_time ? at(session.virtual_end_time) : null;
  if (!start?.isValid || !end?.isValid) {
    start = toChicago(session.start_time);
    end = toChicago(session.end_time);
  } else if (end <= start) end = end.plus({ days: 1 });
  return { start, end, live: !!(start && end && now >= start && now < end), ended: !!(end && now >= end) };
}

/** "just now", "5 min ago", "2 hr ago", "yesterday", "3 days ago", "Oct 2". */
export function ago(value, now = chicagoNow()) {
  const dt = toChicago(value);
  if (!dt) return '';
  const mins = Math.max(0, Math.floor(now.diff(dt, 'minutes').minutes));
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs} hr ago`;
  const days = Math.round(now.startOf('day').diff(dt.startOf('day'), 'days').days);
  if (days <= 1) return 'yesterday';
  if (days < 7) return `${days} days ago`;
  return dt.toFormat('LLL d');
}

const shortName = (first, last) => {
  const f = (first || '').trim();
  const l = (last || '').trim();
  if (!f && !l) return 'A student';
  return l ? `${f} ${l[0]}.` : f;
};

/* ----------------------------- Live Q&A lane ----------------------------- */

/** Questions with no reply yet (and not marked answered), newest first. `threads` = [{ session, questions }]. */
export function buildQnaRows(threads, now = chicagoNow()) {
  const rows = [];
  (threads || []).forEach(({ session, questions }) => {
    (questions || []).forEach((q) => {
      if (isAnswered(q)) return;
      rows.push({
        id: `qna-${q.id}`,
        session,
        title: `“${String(q.body || '').trim()}”`,
        meta: `${q.isAnonymous ? 'Anonymous' : q.authorName || 'A student'} · ${ago(q.createdAt, now)}`,
        at: toChicago(q.createdAt)?.toMillis() || 0,
      });
    });
  });
  return rows.sort((a, b) => b.at - a.at);
}

/* ------------------------- Extension requests lane ------------------------- */

export function buildExtensionRows(requests, now = chicagoNow()) {
  return (requests || [])
    .filter((r) => r.status === 'pending')
    .map((r) => ({
      id: `ext-${r.id}`,
      request: r,
      title: `${shortName(r.student_first_name, r.student_last_name)} · ${r.item_title || (r.item_type === 'quiz' ? 'Quiz' : 'Assignment')}`,
      meta: [r.course_code, `asked ${ago(r.requested_at, now)}`].filter(Boolean).join(' · '),
      at: toChicago(r.requested_at)?.toMillis() || 0,
    }))
    .sort((a, b) => a.at - b.at); // oldest request first: it's been waiting longest
}

/* ------------------------------ To grade lane ------------------------------ */

/**
 * Assignments (GET /assignments, instructor counts) and assigned quizzes
 * (GET /quizzes/assigned) with something waiting to be graded, oldest first.
 */
export function buildGradingRows(assignments, quizzes) {
  const rows = [];
  collapseWeeks(assignments || []).forEach((a) => {
    const n = num(a.to_grade_count);
    if (n <= 0) return;
    rows.push({
      id: `grade-a-${a.id}`,
      kind: 'assignment',
      courseId: a.course_id,
      courseCode: a.course_code || '',
      title: a.title ? String(a.title).replace(/<[^>]*>/g, '').trim() : 'Assignment',
      count: n,
      href: `/assignments/${a.id}/submissions`,
      at: toChicago(a.oldest_ungraded_at)?.toMillis() ?? Number.MAX_SAFE_INTEGER,
    });
  });
  (quizzes || []).forEach((q) => {
    const n = num(q.needs_grading_count);
    if (n <= 0) return;
    rows.push({
      id: `grade-q-${q.assignment_id || `${q.quiz_id}-${q.course_id}`}`,
      kind: 'quiz',
      courseId: q.course_id,
      courseCode: q.course_code || '',
      title: q.quiz_title || 'Quiz',
      count: n,
      href: `/instructor/quizzes/${q.quiz_id}/attempts`,
      at: toChicago(q.oldest_pending_at)?.toMillis() ?? Number.MAX_SAFE_INTEGER,
    });
  });
  return rows
    .sort((a, b) => a.at - b.at)
    .map((r) => ({ ...r, meta: [r.courseCode, `${r.count} waiting`].filter(Boolean).join(' · ') }));
}

export const sumCounts = (rows) => rows.reduce((s, r) => s + (r.count || 0), 0);

/* ------------------------------ Courses & weeks ----------------------------- */

const dateOnly = (v) => {
  if (!v) return null;
  if (v instanceof Date) return DateTime.fromJSDate(v, { zone: 'utc' }).toISODate();
  return String(v).slice(0, 10);
};

/** Course start (Chicago midnight of its start date). */
export function courseStart(course) {
  const d = dateOnly(course?.start_date);
  if (!d) return null;
  const dt = DateTime.fromISO(d, { zone: ZONE });
  return dt.isValid ? dt : null;
}

/** Number of weeks the course runs (1–52); 12 when there's no end date. */
export function totalWeeks(course) {
  const start = courseStart(course);
  const endD = dateOnly(course?.end_date);
  const end = endD ? DateTime.fromISO(endD, { zone: ZONE }) : null;
  if (!start || !end?.isValid || end < start) return 12;
  return Math.min(52, Math.max(1, Math.ceil((end.diff(start, 'days').days + 1) / 7)));
}

/** Week of the course we're in now (1-based, clamped to the course length). */
export function currentWeek(course, now = chicagoNow()) {
  const start = courseStart(course);
  if (!start || now < start) return 1;
  const w = Math.floor(now.diff(start, 'days').days / 7) + 1;
  return Math.min(totalWeeks(course), Math.max(1, w));
}

const WEEKDAY_CODES = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun']; // luxon weekday 1 = Monday

/** The weekdays the course meets (luxon numbers), from its saved meeting days; else the start date's weekday. */
function meetingWeekdays(course, start) {
  const saved = String(course?.meeting_days || '').split(',').map((d) => d.trim().toLowerCase());
  const days = WEEKDAY_CODES.map((code, i) => (saved.includes(code) ? i + 1 : null)).filter(Boolean);
  return days.length > 0 ? days : [start.weekday];
}

/**
 * The upcoming class days from now, soonest first: [{ at: DateTime, canceled, movedTo }]. A canceled day is listed
 * (marked) so a teacher is told; a class moved onto another day is listed on that day.
 * `cancellations`: [{ date, movedTo }] for this course.
 */
export function upcomingClasses(course, now = chicagoNow(), cancellations = []) {
  const start = courseStart(course);
  const t = course?.virtual_start_time ? String(course.virtual_start_time).slice(0, 5) : null;
  if (!start || !t) return [];
  const [h, m] = t.split(':').map(Number);
  const weekdays = meetingWeekdays(course, start);
  const endD = dateOnly(course?.end_date);
  const canceled = new Map(cancellations.map((c) => [c.date, c]));
  const movedOnto = new Set(cancellations.filter((c) => c.movedTo).map((c) => c.movedTo));
  const out = [];
  let day = now < start ? start : now.startOf('day');
  for (let i = 0; i < 28 && out.length < 6; i += 1) {
    const cand = day.set({ hour: h, minute: m });
    const key = cand.toISODate();
    const regular = weekdays.includes(cand.weekday) && cand >= start && (!endD || key <= endD);
    if ((regular || movedOnto.has(key)) && cand > now) {
      const c = canceled.get(key);
      out.push({ at: cand, canceled: Boolean(c) && !movedOnto.has(key), movedTo: c?.movedTo || null });
    }
    day = day.plus({ days: 1 });
  }
  return out;
}

/** Next class time: the next class that is not canceled (the course's meeting days at virtual_start_time). */
export function nextClass(course, now = chicagoNow(), cancellations = []) {
  return upcomingClasses(course, now, cancellations).find((c) => !c.canceled)?.at || null;
}

const whenWord = (at, now) => {
  const diff = Math.round(at.startOf('day').diff(now.startOf('day'), 'days').days);
  return diff === 0 ? 'today' : diff === 1 ? 'tomorrow' : at.toFormat('ccc');
};

/**
 * "Next class Tue 7:00 PM · Week 8" (or "Week 3" when there's no class time). When the next class day is canceled:
 * "Class Tue canceled · next Tue 7:00 PM" (or "moved to Sat, Jan 17").
 */
export function nextClassLine(course, now = chicagoNow(), cancellations = []) {
  const [first] = upcomingClasses(course, now, cancellations);
  if (first?.canceled) {
    const next = nextClass(course, now, cancellations);
    const what = first.movedTo ? `moved to ${DateTime.fromISO(first.movedTo, { zone: ZONE }).toFormat('ccc, LLL d')}` : 'canceled';
    const after = first.movedTo || !next ? '' : ` · next ${whenWord(next, now)} ${next.toFormat('h:mm a')}`;
    return `Class ${whenWord(first.at, now)} ${what}${after}`;
  }
  const next = first?.at || null;
  if (!next) return `Week ${currentWeek(course, now)}`;
  return `Next class ${whenWord(next, now)} ${next.toFormat('h:mm a')} · Week ${currentWeek(course, next)}`;
}

/** Which course Quick Add should aim at: the live one, else the next class. */
export function pickDefaultCourse(courses, liveCourseId, now = chicagoNow()) {
  if (!courses?.length) return null;
  if (liveCourseId) {
    const live = courses.find((c) => c.id === liveCourseId);
    if (live) return live;
  }
  const withNext = courses
    .map((c) => ({ c, next: nextClass(c, now) }))
    .filter((x) => x.next)
    .sort((a, b) => a.next - b.next);
  return (withNext[0] || { c: courses[0] }).c;
}

/** Week number from a session title like "Week 7 - The Divine Attributes". */
export const weekFromTitle = (title) => {
  const m = /week\s*(\d+)/i.exec(title || '');
  return m ? Number(m[1]) : null;
};
